import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { serviceCategories } from '../data/services';
import { practice } from '../data/site';
import { SectionLabel } from './ui/SectionLabel';
import { Reveal } from './ui/Reveal';
export function ServicesSection() {
  const [active, setActive] = useState(serviceCategories[0].id);
  const reduce = useReducedMotion();
  const category = serviceCategories.find(c => c.id === active) ?? serviceCategories[0];
  const featured = category.services.find(s => s.feature) ?? category.services[0];
  const rest = category.services.filter(s => s !== featured);
  return <section id="services" className="scroll-mt-24 bg-white py-24 lg:py-40" data-id="element-423" data-mp="22jzzf28us2w7">
      <div className="mx-auto max-w-shell px-5 sm:px-8 lg:px-12" data-id="element-424" data-mp="1nlxqecj1yp7k">
        <Reveal className="max-w-[820px]" data-id="element-425" data-mp="1nnr0k1svjrkb">
          <SectionLabel data-id="element-426" data-mp="1sl0n99ovpp4g">Our services</SectionLabel>
          <h2 className="font-display mt-6 text-[clamp(2rem,4.2vw,3.3rem)] leading-[1.06] text-ink" data-id="element-427" data-mp="1smv8yq7z5l9l">
            All the care your smile
            <br data-id="element-428" data-mp="2kopxx5jchxwj" />
            needs, in one place.
          </h2>
          <p className="mt-6 max-w-[58ch] text-[16.5px] leading-[1.8] text-ink/65" data-id="element-429" data-mp="2kqawr7cp0307">
            From prevention and restoration to cosmetic treatment and emergency care, our team is
            here to look after your smile at every stage.
          </p>
        </Reveal>

        <div role="tablist" aria-label="Service categories" className="no-scrollbar mt-12 flex gap-8 overflow-x-auto border-b border-ink/10 lg:mt-16" data-id="element-430" data-mp="26xbeixlizuoy">
          {serviceCategories.map(cat => {
          const isActive = cat.id === active;
          return <button key={cat.id} role="tab" id={`service-tab-${cat.id}`} aria-selected={isActive} aria-controls={`service-panel-${cat.id}`} onClick={() => setActive(cat.id)} className={`relative min-h-[48px] shrink-0 pb-4 text-[15px] font-medium transition-colors ${isActive ? 'text-ink' : 'text-ink/45 hover:text-ink/75'}`} data-id="element-431" data-mp="2awtwpllypro3">
                {cat.label}
                {isActive && <motion.span layoutId="services-underline" aria-hidden="true" className="absolute inset-x-0 -bottom-px h-[2px] bg-accent" transition={{
              duration: reduce ? 0 : 0.35,
              ease: [0.2, 0.8, 0.2, 1]
            }} data-id="element-432" data-mp="w0ob97wooea" />}
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-433" data-mp="3ea41k33m18j2">
          <motion.div key={category.id} id={`service-panel-${category.id}`} role="tabpanel" aria-labelledby={`service-tab-${category.id}`} initial={reduce ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? undefined : {
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: [0.2, 0.8, 0.2, 1]
        }} className="mt-12 lg:mt-16" data-id="element-434" data-mp="3eb5de9sg9z1u">
            <p className="max-w-[62ch] text-[16px] leading-[1.8] text-ink/70" data-id="element-435" data-mp="24bufq381bt1c">
              {category.intro}
            </p>

            <div className="mt-10 grid gap-5 lg:grid-cols-12" data-id="element-436" data-mp="1zchxxww0tc5m">
              {/* Featured service with photography */}
              <article className="glass-dark relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-[14px] p-8 text-white lg:col-span-5 lg:min-h-[540px]" data-id="element-437" data-mp="1ufj4zho3fb90">
                {featured.image && <>
                    <img src={featured.image} alt={featured.imageAlt ?? featured.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[55%_top]" data-id="element-438" data-mp="1georwlq3om40" />
                    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/15" data-id="element-439" data-mp="3aqmzkj8rh9xs" />
                  </>}
                <div className="relative" data-id="element-440" data-mp="1yhdqny3nza03">
                  <p className="eyebrow text-white/50" data-id="element-441" data-mp="1ygcic1e191m2">{category.label}</p>
                  <h3 className="font-display mt-4 text-[28px] leading-[1.12] text-white" data-id="element-442" data-mp="23ew4a1smmdef">
                    {featured.name}
                  </h3>
                  <p className="mt-4 max-w-[38ch] text-[15px] leading-relaxed text-white/70" data-id="element-443" data-mp="1op7yoodewskw">
                    {featured.description}
                  </p>
                  <a href={practice.bookingUrl} target="_blank" rel="noreferrer" className="group mt-7 inline-flex min-h-[44px] items-center gap-2 text-[14px] font-medium text-white" data-id="element-444" data-mp="1frnvvazwio86">
                    <span className="nav-link" data-id="element-445" data-mp="1aq8bzm9gmnrd">Book an appointment</span>
                    <span className="hover-arrow text-accent" data-id="element-446" data-mp="r5831asts23a">
                      <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2} data-id="element-447" data-mp="r5rkyrih6qhb" />
                    </span>
                  </a>
                </div>
              </article>

              {/* Remaining services in this category */}
              <ul className="grid gap-4 sm:grid-cols-2 lg:col-span-7 lg:content-start" data-id="element-448" data-mp="1a3u5at02vz8s">
                {rest.map(service => <li key={service.name} className="rounded-[12px] border border-ink/[0.09] bg-paper-off/60 p-6 transition-colors hover:border-ink/25" data-id="element-449" data-mp="161z7hyhcw3zr">
                    <h3 className="text-[17px] font-semibold leading-snug text-ink" data-id="element-450" data-mp="1evlwn5r268zz">
                      {service.name}
                    </h3>
                    <p className="mt-2.5 text-[15px] leading-relaxed text-ink/65" data-id="element-451" data-mp="1oti8y0vqf9zo">
                      {service.description}
                    </p>
                  </li>)}

                {category.id === 'emergency' && <li className="rounded-[12px] border border-accent/40 bg-white p-6 sm:col-span-2" data-id="element-452" data-mp="20w2q52qy7t3j">
                    <p className="text-[15.5px] leading-relaxed text-ink" data-id="element-453" data-mp="25t1lfomcm63b">
                      <span className="font-semibold" data-id="element-454" data-mp="2ame6jortjurb">Emergency care:</span> if you have dental
                      pain, a broken tooth or an infection, call Lima Dental Group directly.
                    </p>
                    <a href={practice.phoneHref} className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-[15px] font-semibold text-ink" data-id="element-455" data-mp="2jkpsvelnk6dy">
                      <PhoneIcon aria-hidden="true" className="h-4 w-4 text-accent" strokeWidth={1.75} data-id="element-456" data-mp="2tjovafftr5si" />
                      {practice.phone}
                    </a>
                  </li>}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}